"use client"

import React, { useCallback, useEffect, useState } from 'react'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { useRegionWorkScopes, WorkScopeWithStatus } from '../../hooks/use-region-work-scopes'
import { Switch } from '@/shared/components/ui/switch'
import { Loader2 } from 'lucide-react'
import errorToast from '@/shared/lib/toast/error.toast'
import successToast from '@/shared/lib/toast/success.toast'
import LocationSelector from '@/shared/components/location-selector'
import ToolsSuppliesManager from '@/features/work-orders/components/forms/tools-supplies-manager'
import FormCard from '@/features/work-orders/ui/form-card'
import { cn } from '@/shared/lib/utils'

function WorkScopesTab() {
  const { regionId, region } = useRegionId()
  const { workScopes, loading, error, toggleWorkScope, updating, refetch } =
    useRegionWorkScopes()
  const [selectedWorkScopeId, setSelectedWorkScopeId] = useState<number | null>(null)
  const [toolsSuppliesDirty, setToolsSuppliesDirty] = useState(false)

  const selectedScope = workScopes.find((ws) => ws.id === selectedWorkScopeId) ?? null

  useEffect(() => {
    setSelectedWorkScopeId(null)
    setToolsSuppliesDirty(false)
  }, [regionId])

  const confirmDiscardChanges = useCallback(() => {
    if (!toolsSuppliesDirty) return true
    return window.confirm(
      'You have unsaved tools and supplies changes. Discard them and continue?'
    )
  }, [toolsSuppliesDirty])

  const handleSelectScope = (workScopeId: number) => {
    if (workScopeId === selectedWorkScopeId) return
    if (!confirmDiscardChanges()) return
    setSelectedWorkScopeId(workScopeId)
    setToolsSuppliesDirty(false)
  }

  const handleToggle = async (workScope: WorkScopeWithStatus) => {
    const newActive = !workScope.active
    try {
      await toggleWorkScope(workScope.id, newActive)
      successToast(`Work scope ${newActive ? 'enabled' : 'disabled'} successfully`)
    } catch {
      errorToast('Failed to update work scope status')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="border rounded px-4 py-5">
        <p className="text-destructive">{error}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start">
        <div>
          <h3 className="text-lg font-semibold">
            {region?.name ? `${region.name} Work Scopes` : 'Work Scopes'}
          </h3>
          <p className="text-muted-foreground text-sm mt-1">
            Enable or disable work scopes for this region and manage the default tools and supplies used when creating work orders.
          </p>
        </div>
        <LocationSelector />
      </div>

      {!regionId ? (
        <FormCard>
          <div className="text-center py-12 text-muted-foreground">
            <p>Select a region to manage work scopes.</p>
          </div>
        </FormCard>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
          {/* Master list */}
          <div className="w-full lg:w-[min(100%,20rem)] lg:shrink-0 border rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Work scopes
            </div>
            <div className="divide-y max-h-[min(70vh,32rem)] overflow-y-auto">
              {workScopes.length === 0 ? (
                <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                  No work scopes found
                </div>
              ) : (
                workScopes.map((workScope) => {
                  const isSelected = workScope.id === selectedWorkScopeId
                  const isUpdating = updating.has(workScope.id)
                  return (
                    <div
                      key={workScope.id}
                      className={cn(
                        'flex items-start gap-2 px-3 py-3 transition-colors',
                        isSelected && 'bg-primary/5',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => handleSelectScope(workScope.id)}
                        className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                      >
                        <div
                          className={cn(
                            'font-medium text-sm leading-snug',
                            !workScope.active && 'text-muted-foreground',
                          )}
                        >
                          {workScope.workScopeDisplay || workScope.workScope}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {workScope.toolsCount} tool{workScope.toolsCount === 1 ? '' : 's'}
                          {' · '}
                          {workScope.suppliesCount} suppl{workScope.suppliesCount === 1 ? 'y' : 'ies'}
                        </div>
                      </button>
                      <div className="flex items-center gap-2 shrink-0 pt-0.5">
                        {isUpdating && (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                        <Switch
                          checked={workScope.active}
                          onCheckedChange={() => {
                            void handleToggle(workScope)
                          }}
                          disabled={isUpdating}
                          aria-label={`Toggle ${workScope.workScopeDisplay}`}
                        />
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>

          {/* Detail panel */}
          <div className="flex-1 min-w-0 space-y-4">
            {selectedScope ? (
              <>
                <div className="space-y-1">
                  <h4 className="text-base font-semibold">
                    {selectedScope.workScopeDisplay || selectedScope.workScope}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Default tools and supplies for new work orders in this region.
                  </p>
                </div>
                <ToolsSuppliesManager
                  key={`${regionId}-${selectedScope.workScope}`}
                  workOrderType={selectedScope.workScope}
                  regionId={regionId}
                  embedded
                  scopeLabel={selectedScope.workScopeDisplay}
                  scopeActive={selectedScope.active}
                  onUnsavedChangesChange={setToolsSuppliesDirty}
                  onSaved={refetch}
                />
              </>
            ) : (
              <FormCard>
                <div className="text-center py-16 text-muted-foreground">
                  <p className="text-sm">
                    Select a work scope to edit its default tools and supplies.
                  </p>
                </div>
              </FormCard>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default WorkScopesTab
